import React, { useEffect, useRef, useState } from "react";
import { api, API, formatEUR } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Mail, Phone, User, FolderOpen, FileText, Upload, X, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";

const emptyForm = { nom: "", email: "", telephone: "", adresse: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [factures, setFactures] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsClient, setDocsClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const docFileInput = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    const [c, f] = await Promise.all([api.get("/clients"), api.get("/factures")]);
    setClients(c.data);
    setFactures(f.data);
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      nom: c.nom || "",
      email: c.email || "",
      telephone: c.telephone || "",
      adresse: c.adresse || "",
      notes: c.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) {
      toast.error("Nom obligatoire");
      return;
    }
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Client mis à jour");
      } else {
        await api.post("/clients", form);
        toast.success("Client créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer le client "${c.nom}" ?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const statsByClient = (clientId) => {
    const list = factures.filter((f) => f.client_id === clientId);
    const du = list.filter((f) => !f.payee).reduce((s, f) => s + Number(f.montant || 0), 0);
    const enRetard = list.filter((f) => f.statut === "en_retard").length;
    return { nb: list.length, du, enRetard };
  };

  const openDocs = async (c) => {
    setDocsClient(c);
    setDocsOpen(true);
    try {
      const { data } = await api.get(`/clients/${c.id}/documents`);
      setDocuments(data);
    } catch (e) {
      setDocuments([]);
    }
  };

  const uploadDocument = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !docsClient) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${docsClient.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document ajouté");
      const { data } = await api.get(`/clients/${docsClient.id}/documents`);
      setDocuments(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = async (docId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/files/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      toast.success("Document supprimé");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="clients-page" className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Clients
          </h1>
          <p className="text-[#5C665E] mt-2">
            {clients.length} client{clients.length > 1 ? "s" : ""}
          </p>
        </div>
        <button
          data-testid="create-client-button"
          onClick={openCreate}
          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white btn-lift focus-brand flex items-center gap-2"
          style={{ backgroundColor: "#2C5545" }}
        >
          <Plus size={16} /> Nouveau client
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="rounded-2xl border bg-white p-12 text-center" style={{ borderColor: "#E5E2DC" }}>
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: "#F9F8F6" }}>
            <User size={24} color="#5C665E" />
          </div>
          <p className="text-[#5C665E] mb-4">
            Aucun client pour le moment.
          </p>
          <button
            data-testid="empty-create-client"
            onClick={openCreate}
            className="rounded-full px-4 py-2 text-sm font-semibold text-white btn-lift"
            style={{ backgroundColor: "#2C5545" }}
          >
            Ajouter votre premier client
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => {
            const s = statsByClient(c.id);
            return (
              <div
                key={c.id}
                data-testid={`client-card-${c.id}`}
                className="rounded-2xl border bg-white p-6 flex flex-col gap-4"
                style={{ borderColor: "#E5E2DC" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-semibold text-lg truncate">
                      {c.nom}
                    </h3>
                    <div className="space-y-1 mt-2 text-sm text-[#5C665E]">
                      {c.email && (
                        <div className="flex items-center gap-2 truncate">
                          <Mail size={12} strokeWidth={1.75} />
                          <span className="truncate">{c.email}</span>
                        </div>
                      )}
                      {c.telephone && (
                        <div className="flex items-center gap-2">
                          <Phone size={12} strokeWidth={1.75} />
                          <span>{c.telephone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      data-testid={`edit-client-${c.id}`}
                      onClick={() => openEdit(c)}
                      className="p-1.5 rounded-lg text-[#5C665E] hover:bg-[#F9F8F6] btn-lift"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      data-testid={`delete-client-${c.id}`}
                      onClick={() => remove(c)}
                      className="p-1.5 rounded-lg text-[#B3261E] hover:bg-[#FCE8E8] btn-lift"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-4 border-t" style={{ borderColor: "#E5E2DC" }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C665E]">Factures</div>
                    <div className="font-mono-num font-semibold text-sm">{s.nb}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C665E]">Dû</div>
                    <div className="font-mono-num font-semibold text-sm">{formatEUR(s.du)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C665E]">Retard</div>
                    <div className={`font-mono-num font-semibold text-sm ${s.enRetard>0 ? 'text-[#B3261E]' : ''}`}>
                      {s.enRetard}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <button
                    data-testid={`view-factures-${c.id}`}
                    onClick={() => navigate(`/factures?client=${c.id}`)}
                    className="text-[#2C5545] font-semibold hover:underline"
                  >
                    Voir les factures →
                  </button>
                  <button
                    data-testid={`view-documents-${c.id}`}
                    onClick={() => openDocs(c)}
                    className="text-[#5C665E] font-semibold hover:text-[#1A1D1A] flex items-center gap-1"
                  >
                    <FolderOpen size={12} /> Documents
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Client edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Modifier le client" : "Nouveau client"}
            </DialogTitle>
            <DialogDescription className="text-[#5C665E]">
              Renseignez les coordonnées de votre client.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Nom *</Label>
              <Input
                data-testid="client-form-nom"
                value={form.nom}
                onChange={(e) => setForm({ ...form, nom: e.target.value })}
                required
                autoFocus
                className="bg-white border-[#E5E2DC]"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Email</Label>
                <Input
                  data-testid="client-form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white border-[#E5E2DC]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Téléphone</Label>
                <Input
                  data-testid="client-form-telephone"
                  value={form.telephone}
                  onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                  className="bg-white border-[#E5E2DC]"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Adresse</Label>
              <Textarea
                data-testid="client-form-adresse"
                value={form.adresse}
                onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                className="bg-white border-[#E5E2DC]"
                rows={2}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Notes</Label>
              <Textarea
                data-testid="client-form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-white border-[#E5E2DC]"
                rows={2}
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="client-form-cancel"
                className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#5C665E] hover:bg-[#F9F8F6] btn-lift"
              >
                Annuler
              </button>
              <button
                type="submit"
                data-testid="client-form-submit"
                className="rounded-full px-6 py-2.5 text-sm font-semibold text-white btn-lift focus-brand"
                style={{ backgroundColor: "#2C5545" }}
              >
                {editing ? "Enregistrer" : "Créer"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents dialog */}
      <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
        <DialogContent className="bg-white sm:max-w-xl" data-testid="documents-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Documents · {docsClient?.nom}
            </DialogTitle>
            <DialogDescription className="text-[#5C665E]">
              Devis, contrats, ou tout document lié à ce client.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <button
              type="button"
              data-testid="upload-client-doc-button"
              onClick={() => docFileInput.current?.click()}
              disabled={uploading}
              className="w-full rounded-lg border border-dashed p-4 text-sm text-[#5C665E] hover:bg-[#F9F8F6] btn-lift flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ borderColor: "#E5E2DC" }}
            >
              <Upload size={14} />
              {uploading ? "Envoi en cours..." : "Ajouter un document (PDF)"}
            </button>
            <input
              ref={docFileInput}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={uploadDocument}
              data-testid="client-doc-input"
            />

            {documents.length === 0 ? (
              <p className="text-center text-sm text-[#5C665E] py-6">
                Aucun document pour ce client.
              </p>
            ) : (
              <ul className="divide-y" style={{ borderColor: "#E5E2DC" }}>
                {documents.map((d) => (
                  <li
                    key={d.id}
                    className="py-3 flex items-center justify-between gap-3"
                    data-testid={`doc-item-${d.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: d.category === "facture_pdf" ? "#FFF2D1" : "#DFF2E4",
                          color: d.category === "facture_pdf" ? "#8C6100" : "#1E6534",
                        }}
                      >
                        <FileText size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {d.original_filename || "Document"}
                        </div>
                        <div className="text-xs text-[#5C665E] flex items-center gap-1.5">
                          {d.category === "facture_pdf" && d.facture_numero && (
                            <span
                              className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
                              style={{ backgroundColor: "#FFF2D1", color: "#8C6100" }}
                            >
                              Facture {d.facture_numero}
                            </span>
                          )}
                          <span>{Math.round((d.size || 0) / 1024)} Ko</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <a
                        href={`${API}/files/${d.id}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`view-doc-${d.id}`}
                        className="p-2 rounded-lg text-[#5C665E] hover:bg-[#F9F8F6] btn-lift"
                        title="Ouvrir"
                      >
                        <Eye size={14} />
                      </a>
                      <button
                        type="button"
                        data-testid={`delete-doc-${d.id}`}
                        onClick={() => removeDocument(d.id)}
                        className="p-2 rounded-lg text-[#B3261E] hover:bg-[#FCE8E8] btn-lift"
                        title="Supprimer"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
